// Thin fetch wrapper for the parts-finder API, shared by all widgets.

import { getAppBaseUrl, getApiKey } from './config.js';

/**
 * Fetch `path` (absolute path like "/api/parts/LM6261") against the app base URL,
 * always attaching the Bearer key. `opts` is merged over sensible defaults;
 * `opts.headers` is merged (not replaced). Returns the raw Response.
 */
export function apiFetch(path, opts) {
  opts = opts || {};
  var base = getAppBaseUrl().replace(/\/$/, '');
  var headers = Object.assign(
    { 'Accept': 'application/json', 'Authorization': 'Bearer ' + getApiKey() },
    opts.headers || {}
  );
  var init = Object.assign({ method: 'GET' }, opts, { headers: headers });
  return fetch(base + path, init);
}

// ── GET de-duplication + brief cache ──────────────────────────────────────
// On a product detail page a dozen widgets initialise independently and several
// fetch the SAME data — most notably the base part document (/api/parts/:sku,
// pulled by product-price, oem-codes, additional-info, videos, vehicle-
// compatibility, …) and the branch list (/api/branches). Left alone the PDP
// makes each of those calls 4-8×, and every one is a cross-origin round trip
// through the iframe proxy. We coalesce identical in-flight GETs and cache the
// parsed JSON for a short window so each distinct call happens ONCE per load.
//
// Safety: the key includes the caller's identity headers so different roles /
// sessions never share a response; the TTL is short so nothing goes stale after
// a user action; failures are evicted so they retry; and callers that must
// bypass can pass `opts.noDedupe = true`.
var _getCache = new Map(); // key -> { ts, promise }  (promise resolves to parsed JSON, or rejects)
var GET_CACHE_TTL_MS = 10000;

function getKey(path, headers) {
  var cf = (headers && headers['X-Commerce-Features']) || '';
  var sr = (headers && headers['X-Session-Reference']) || '';
  return path + '\n' + cf + '\n' + sr;
}

/**
 * apiFetch + parse JSON, with in-flight de-duplication and a brief cache for
 * idempotent GETs. Resolves to `fallback` (default null) on any non-OK / error.
 */
export function apiJson(path, opts, fallback) {
  opts = opts || {};
  var fb = fallback !== undefined ? fallback : null;
  var method = (opts.method || 'GET').toUpperCase();

  // Only GETs are safe to share/cache; anything else (or an explicit opt-out)
  // goes straight through.
  if (method !== 'GET' || opts.noDedupe) {
    return apiFetch(path, opts)
      .then(function (r) { return r.ok ? r.json() : fb; })
      .catch(function () { return fb; });
  }

  var key = getKey(path, opts.headers);
  var now = Date.now();
  var hit = _getCache.get(key);
  if (!hit || (now - hit.ts) >= GET_CACHE_TTL_MS) {
    var p = apiFetch(path, opts).then(function (r) {
      if (!r.ok) throw new Error('http ' + r.status);
      return r.json();
    });
    // Don't cache failures — evict so the next caller retries.
    p.catch(function () {
      var cur = _getCache.get(key);
      if (cur && cur.promise === p) _getCache.delete(key);
    });
    hit = { ts: now, promise: p };
    _getCache.set(key, hit);
  }
  // Each caller gets the fallback on error; a null body also falls back.
  return hit.promise.then(
    function (v) { return v == null ? fb : v; },
    function () { return fb; }
  );
}
